/**
 * Returns the top 3 most extreme traits using upperThird as the benchmark.
 * If type === "bar", upperThird is the sum of upperThird, middleThird, and lowerThird.
 * Logs calculation values for debugging.
 * @param {Array} traitData
 * @param {string} type
 * @returns {Array} Top 3 traits, sorted by extremity (most extreme first)
 */
export function getTopExtremeTraitsUpperThird(traitData, type) {
  if (!Array.isArray(traitData) || traitData.length < 3) return [];

  // Helper to get userScore as number
  const getUserScore = trait => typeof trait.user === "number" ? trait.user : parseFloat(trait.user);

  // Exclude "Cognitive style"
  const filteredTraits = traitData.filter(trait => trait.fr !== "Style cognitif");

  // First, find traits outside the middle third (extreme)
  const traitsWithExtremity = filteredTraits.map((trait) => {
    const userScore = getUserScore(trait);
    let getMiddleUpper, getMiddleBottom;
    if (type === "bar") {
      const mt = typeof trait.middleThird === "number" ? trait.middleThird : parseFloat(trait.middleThird);
      const lt = typeof trait.lowerThird === "number" ? trait.lowerThird : parseFloat(trait.lowerThird);
      getMiddleUpper = mt + lt;
      getMiddleBottom = lt;
      if (userScore > getMiddleBottom && userScore < getMiddleUpper) {
        return null;
      }
      let extremity, extremeLabel;
      if (userScore >= getMiddleUpper) {
        extremity = Math.abs(userScore - getMiddleUpper);
        extremeLabel = "upperThird";
      } else if (userScore <= getMiddleBottom) {
        extremity = Math.abs(userScore - getMiddleBottom);
        extremeLabel = "lowerThird";
      } else {
        return null;
      }
      return { ...trait, extremity, extremeLabel };
    } else {
      getMiddleUpper = typeof trait.middleThird === "number" ? trait.middleThird : parseFloat(trait.middleThird);
      getMiddleBottom = typeof trait.lowerThird === "number" ? trait.lowerThird : parseFloat(trait.lowerThird);
      if (userScore > getMiddleBottom && userScore < getMiddleUpper) {
        return null;
      }
      let extremity, extremeLabel;
      if (userScore >= getMiddleUpper) {
        extremity = Math.abs(userScore - getMiddleUpper);
        extremeLabel = "upperThird"; // FIXED: was "lowerThird"
      } else if (userScore <= getMiddleBottom) {
        extremity = Math.abs(userScore - getMiddleBottom);
        extremeLabel = "lowerThird"; // FIXED: was "upperThird"
      } else {
        return null;
      }
      return { ...trait, extremity, extremeLabel };
    }
  }).filter(Boolean);

  // Sort by extremity descending
  const sorted = traitsWithExtremity.sort((a, b) => b.extremity - a.extremity);

  // If we have 3 or more, return top 3
  if (sorted.length >= 3) return sorted.slice(0, 3);

  // Otherwise, fill with traits from the middle third, furthest from center
  const needed = 3 - sorted.length;
  // Find traits in the middle third
  const middleTraits = filteredTraits
    .filter(trait => {
      const userScore = getUserScore(trait);
      let getMiddleUpper, getMiddleBottom;
      if (type === "bar") {
        const mt = typeof trait.middleThird === "number" ? trait.middleThird : parseFloat(trait.middleThird);
        const lt = typeof trait.lowerThird === "number" ? trait.lowerThird : parseFloat(trait.lowerThird);
        getMiddleUpper = mt + lt;
        getMiddleBottom = lt;
      } else {
        getMiddleUpper = typeof trait.middleThird === "number" ? trait.middleThird : parseFloat(trait.middleThird);
        getMiddleBottom = typeof trait.lowerThird === "number" ? trait.lowerThird : parseFloat(trait.lowerThird);
      }
      return userScore > getMiddleBottom && userScore < getMiddleUpper;
    })
    .map(trait => {
      const userScore = getUserScore(trait);
      let getMiddleUpper, getMiddleBottom, middleCenter;
      if (type === "bar") {
        const mt = typeof trait.middleThird === "number" ? trait.middleThird : parseFloat(trait.middleThird);
        const lt = typeof trait.lowerThird === "number" ? trait.lowerThird : parseFloat(trait.lowerThird);
        getMiddleUpper = mt + lt;
        getMiddleBottom = lt;
        middleCenter = (getMiddleUpper + getMiddleBottom) / 2;
      } else {
        getMiddleUpper = typeof trait.middleThird === "number" ? trait.middleThird : parseFloat(trait.middleThird);
        getMiddleBottom = typeof trait.lowerThird === "number" ? trait.lowerThird : parseFloat(trait.lowerThird);
        middleCenter = (getMiddleUpper + getMiddleBottom) / 2;
      }
      // Distance from center of middle third
      const extremity = Math.abs(userScore - middleCenter);
      return { ...trait, extremity, extremeLabel: "middleThird" };
    })
    .sort((a, b) => b.extremity - a.extremity);

  // Combine and return up to 3
  return [...sorted, ...middleTraits.slice(0, needed)].slice(0, 3);
}